import { useEffect } from "react";
import { useLocation, useParams, matchPath } from "react-router-dom";
import { applyShareMeta } from "@/lib/share-meta";

const SITE = "https://promptwizer.com";
const OG_IMAGE = `${SITE}/og-image.jpg`;

interface MetaDef {
  title: string;
  description: string;
}

const STATIC_META: Record<string, MetaDef> = {
  "/": {
    title: "Promptwizer — Expert AI Prompt Engineering",
    description:
      "Turn vague research ideas into structured Deep Research prompts for ChatGPT, Perplexity, Gemini, and Claude — guided by AI, ready in minutes.",
  },
  "/chat": {
    title: "Research Chat · Promptwizer",
    description:
      "Work with two AI agents to build a structured Deep Research prompt step-by-step. Get a tailored result for ChatGPT, Perplexity, Gemini, or Claude in minutes.",
  },
  "/auth": {
    title: "Sign in · Promptwizer",
    description:
      "Sign in or create a free Promptwizer account to start building structured AI Deep Research prompts, save your history, and unlock plan features.",
  },
  "/subscription": {
    title: "Plans & Pricing · Promptwizer",
    description:
      "Compare Promptwizer plans — Free, Starter, Pro, Team, and Max. Choose the right monthly or annual plan for your Deep Research prompt engineering workflow.",
  },
  "/settings": {
    title: "Settings · Promptwizer",
    description:
      "Manage your Promptwizer account: update your profile, change your password, configure prompt personas, and review your team and billing settings.",
  },
  "/faq": {
    title: "Frequently Asked Questions · Promptwizer",
    description:
      "Answers to common questions about Promptwizer — how it works, what makes it different, how to get started, and how to save AI credits with better prompts.",
  },
  "/about": {
    title: "About Promptwizer — AI Deep Research Prompt Builder",
    description:
      "Learn how Promptwizer helps researchers, consultants, lawyers, and students turn vague ideas into precise AI research prompts.",
  },
  "/contact": {
    title: "Contact · Promptwizer",
    description:
      "Send a message to the Promptwizer team. We're happy to answer questions about prompt engineering, plans, team features, or anything else on your mind.",
  },
  "/consultation": {
    title: "Book a Consultation · Promptwizer",
    description:
      "Book a 1:1 prompt-engineering consultation with the Promptwizer team. Get personalized guidance for your research workflow, use case, or AI strategy.",
  },
  "/for": {
    title: "Who Promptwizer is for · Promptwizer",
    description:
      "Discover how Promptwizer builds tailored Deep Research prompts for VCs, lawyers, academics, and consultants — structured prompts for every professional context.",
  },
  "/gallery": {
    title: "Prompt Gallery · Promptwizer",
    description:
      "Browse public Deep Research prompts shared by the Promptwizer community. Filter by AI tool — ChatGPT, Perplexity, Gemini, or Claude — and find inspiration.",
  },
  "/verify-email": {
    title: "Verify Your Email · Promptwizer",
    description:
      "Check your inbox and click the verification link to activate your Promptwizer account.",
  },
  "/verify-success": {
    title: "Email Verified · Promptwizer",
    description:
      "Your Promptwizer email address has been verified. You can now sign in and start building structured Deep Research prompts.",
  },
};

const VERTICAL_LABEL: Record<string, string> = {
  vcs: "VCs",
  lawyers: "Lawyers",
  academics: "Academics",
  consultants: "Consultants",
};

const NOINDEX_PATTERNS = [
  "/admin",
  "/settings",
  "/settings/personas",
  "/team",
  "/team/builds",
  "/team/accept/:token",
  "/extension/authorize",
  "/email-prefs",
  "/verify-email",
  "/verify-success",
];

// Routes that manage their own <head> tags via applyShareMeta — RouteMeta
// must not run on these or it clobbers per-share titles with the homepage.
const SKIP_PATTERNS = ["/p/:shortId"];

export const RouteMeta = () => {
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    const path = location.pathname;
    if (SKIP_PATTERNS.some((p) => matchPath(p, path))) return;
    let meta: MetaDef | undefined = STATIC_META[path];

    const verticalMatch = matchPath("/for/:vertical", path);
    if (verticalMatch) {
      const slug = verticalMatch.params.vertical || "";
      const label = VERTICAL_LABEL[slug] || slug;
      meta = {
        title: `Promptwizer for ${label} — Deep Research Prompts`,
        description: `Structured AI Deep Research prompts crafted for ${label.toLowerCase()}.`,
      };
    }

    if (!meta) {
      meta = STATIC_META["/"];
    }

    const noindex = NOINDEX_PATTERNS.some((p) => matchPath(p, path));
    const canonical = `${SITE}${path === "/" ? "/" : path}`;

    applyShareMeta({
      title: meta.title,
      description: meta.description,
      canonical,
      ogImage: OG_IMAGE,
      noindex,
    });
  }, [location.pathname, params]);

  return null;
};
